import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  FiBarChart2,
  FiCheckCircle,
  FiTrendingUp,
  FiTrendingDown,
  FiUsers,
  FiActivity,
  FiX,
} from 'react-icons/fi';
import { FaTrophy, FaMedal } from 'react-icons/fa';
import { useApi } from '../../hooks/useApi';
import { getProfileImageUrl } from '../../utils/imageUtils';
import { formatSignedUsd } from '../../utils/formatUsd';
import ProfitLoss from './ProfitLoss';

const MASTER_LEADERBOARD_TABS = [
  { id: 'forex-gold', label: 'Forex Gold', brokerType: 'genesis' },
  { id: 'webull', label: 'Webull', brokerType: 'webull' },
];

const RANK_META = {
  1: {
    label: '1st',
    ring: 'ring-[#246BF5]/55 dark:ring-[#4f8cff]/70',
    avatarShadow: 'shadow-[0_0_24px_rgba(36,107,245,0.28)] dark:shadow-[0_0_28px_rgba(36,107,245,0.5)]',
    chip: 'bg-amber-500/15 text-amber-700 border-amber-500/40 shadow-[0_0_14px_rgba(245,158,11,0.18)] dark:bg-amber-400/15 dark:text-amber-300 dark:border-amber-400/40',
    cardBorder: 'border-[#246BF5]/30 dark:border-[#246BF5]/40',
    glowClass: 'lb-glow-blue-strong',
    washLight: 'rgba(36,107,245,0.12)',
    washDark: 'rgba(36,107,245,0.22)',
    delay: '0ms',
  },
  2: {
    label: '2nd',
    ring: 'ring-[#246BF5]/40 dark:ring-[#4f8cff]/45',
    avatarShadow: 'shadow-[0_0_18px_rgba(36,107,245,0.18)] dark:shadow-[0_0_22px_rgba(36,107,245,0.32)]',
    chip: 'bg-slate-500/10 text-slate-600 border-slate-400/40 shadow-[0_0_12px_rgba(148,163,184,0.15)] dark:bg-slate-300/10 dark:text-slate-200 dark:border-slate-300/35',
    cardBorder: 'border-[#246BF5]/20 dark:border-[#246BF5]/25',
    glowClass: 'lb-glow-blue-mid',
    washLight: 'rgba(36,107,245,0.08)',
    washDark: 'rgba(36,107,245,0.14)',
    delay: '80ms',
  },
  3: {
    label: '3rd',
    ring: 'ring-[#246BF5]/30 dark:ring-[#4f8cff]/35',
    avatarShadow: 'shadow-[0_0_16px_rgba(36,107,245,0.14)] dark:shadow-[0_0_20px_rgba(36,107,245,0.24)]',
    chip: 'bg-orange-500/10 text-orange-700 border-orange-500/35 shadow-[0_0_12px_rgba(234,88,12,0.14)] dark:bg-orange-400/10 dark:text-orange-300 dark:border-orange-400/35',
    cardBorder: 'border-[#246BF5]/15 dark:border-[#246BF5]/18',
    glowClass: 'lb-glow-blue-soft',
    washLight: 'rgba(36,107,245,0.06)',
    washDark: 'rgba(36,107,245,0.1)',
    delay: '160ms',
  },
};

/** Webull = brand blue, Genesis / Forex Gold = amber */
const platformLabelClass = (label) => {
  const key = String(label || '').toLowerCase();
  if (key.includes('genesis') || key.includes('forex') || key.includes('gold')) {
    return 'text-amber-600 dark:text-amber-300';
  }
  return 'text-[#246BF5] dark:text-[#4f8cff]';
};

const formatLeaderboardPnl = (value) => {
  const n = Number(value) || 0;
  if (!Number.isFinite(n)) return '—';
  if (n === 0) return '$0';
  return formatSignedUsd(n);
};

const getInitials = (name) => {
  if (!name) return 'TR';
  const parts = String(name).trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
};

const Avatar = ({ trader, size = 'md', ringClass = '', shadowClass = '' }) => {
  const [broken, setBroken] = useState(false);
  const raw = String(trader?.avatar || '').trim();
  React.useEffect(() => setBroken(false), [raw]);

  const sizeClass =
    size === 'lg'
      ? 'h-11 w-11 text-sm sm:h-20 sm:w-20 sm:text-xl'
      : size === 'sm'
        ? 'h-10 w-10 text-xs'
        : 'h-9 w-9 text-[11px] sm:h-14 sm:w-14 sm:text-sm';

  return (
    <div
      className={`relative flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#e8f0ff] ring-2 transition-transform duration-300 group-hover:scale-105 dark:bg-[#0f1420] ${ringClass || 'ring-[#246BF5]/35'} ${shadowClass} ${sizeClass}`}
    >
      {!raw || broken ? (
        <span className="font-semibold tracking-wide text-[#246BF5] dark:text-white/80">
          {getInitials(trader?.name)}
        </span>
      ) : (
        <img
          src={getProfileImageUrl(raw)}
          alt={trader?.name || 'Trader'}
          className="h-full w-full object-cover"
          onError={() => setBroken(true)}
        />
      )}
    </div>
  );
};

const Metric = ({ label, value, tone = 'neutral' }) => {
  const toneClass =
    tone === 'pos'
      ? 'text-emerald-600 dark:text-emerald-400'
      : tone === 'neg'
        ? 'text-red-600 dark:text-red-400'
        : 'text-foreground';

  return (
    <div className="min-w-0 text-center">
      <p className={`truncate text-xs font-semibold tabular-nums sm:text-sm ${toneClass}`}>{value}</p>
      <p className="mt-0.5 text-[9px] font-medium uppercase tracking-[0.14em] text-muted sm:text-[10px]">
        {label}
      </p>
    </div>
  );
};

const LeaderboardMarketTabs = ({ activeTab, onChange }) => (
  <div
    className="inline-flex w-auto rounded-lg border border-[#246BF5]/20 bg-white/80 p-0.5 shadow-sm backdrop-blur-md dark:border-white/10 dark:bg-white/[0.04]"
    role="tablist"
    aria-label="Leaderboard market"
  >
    {MASTER_LEADERBOARD_TABS.map((tab) => {
      const isActive = activeTab === tab.id;
      return (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={isActive}
          onClick={() => onChange(tab.id)}
          className={`rounded-md px-2 py-1 text-[10px] font-semibold transition-colors sm:px-3.5 sm:py-1.5 sm:text-sm ${
            isActive
              ? 'bg-[#246BF5] text-white shadow-[0_4px_12px_rgba(36,107,245,0.3)]'
              : 'text-muted hover:bg-[#246BF5]/8 hover:text-foreground dark:hover:bg-white/[0.06] dark:hover:text-white'
          }`}
        >
          {tab.label}
        </button>
      );
    })}
  </div>
);

const PodiumCard = ({ trader, rank, onViewStats, platformLabel, featured = false }) => {
  const meta = RANK_META[rank] || RANK_META[3];
  const pnl = Number(trader.pnl || 0);
  const roi = Number(trader.roi || 0);
  const winRate = Number(trader.winRate || 0);
  const roiTone = roi >= 0 ? 'pos' : 'neg';
  const pnlTone =
    pnl > 0
      ? 'text-emerald-600 dark:text-emerald-400'
      : pnl < 0
        ? 'text-red-600 dark:text-red-400'
        : 'text-foreground';

  return (
    <div
      className={`group flex w-full min-w-0 flex-col items-center lb-anim-rise ${
        featured ? '-mt-1 sm:-mt-3 md:-mt-6 lb-anim-float' : 'mt-1 sm:mt-3 md:mt-4'
      }`}
      style={{ animationDelay: meta.delay }}
    >
      <article
        className={`relative w-full overflow-hidden rounded-xl border bg-white/80 p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_10px_28px_rgba(36,107,245,0.08)] backdrop-blur-xl transition-transform duration-300 hover:-translate-y-1 dark:bg-white/[0.04] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] sm:rounded-2xl sm:p-5 ${meta.cardBorder} ${meta.glowClass} ${
          featured ? 'md:p-6' : ''
        }`}
      >
        <div
          className="pointer-events-none absolute inset-0 opacity-70 dark:opacity-50"
          style={{
            background: `radial-gradient(circle at 50% 0%, ${meta.washLight}, transparent 55%)`,
          }}
        />
        <div
          className="pointer-events-none absolute inset-0 hidden opacity-50 dark:block"
          style={{
            background: `radial-gradient(circle at 50% 0%, ${meta.washDark}, transparent 55%)`,
          }}
        />

        <div className="relative z-10">
          <div className="mb-1.5 flex items-center justify-between gap-1 sm:mb-4 sm:gap-2">
            <span
              className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] sm:rounded-md sm:gap-1.5 sm:px-2.5 sm:py-1 sm:text-[11px] ${meta.chip}`}
            >
              {rank === 1 ? <FaTrophy className="h-2.5 w-2.5 sm:h-3 sm:w-3" /> : <FaMedal className="h-2.5 w-2.5 sm:h-3 sm:w-3" />}
              {meta.label}
            </span>
            <span
              className={`truncate text-[8px] font-semibold uppercase tracking-[0.1em] sm:text-[11px] ${platformLabelClass(platformLabel)}`}
            >
              {platformLabel}
            </span>
          </div>

          <div className="flex flex-col items-center text-center">
            <Avatar
              trader={trader}
              size={featured ? 'lg' : 'md'}
              ringClass={meta.ring}
              shadowClass={meta.avatarShadow}
            />

            <div className="mt-1.5 flex min-w-0 items-center justify-center gap-1 sm:mt-3 sm:gap-1.5">
              <h3
                className={`truncate font-semibold text-foreground ${
                  featured ? 'text-xs sm:text-xl' : 'text-[11px] sm:text-lg'
                }`}
              >
                {trader.name}
              </h3>
              <FiCheckCircle className="h-3 w-3 shrink-0 text-[#246BF5] dark:text-[#4f8cff] sm:h-4 sm:w-4" aria-label="Verified" />
            </div>

            <p
              className={`mt-1 font-bold tabular-nums tracking-tight sm:mt-3 ${pnlTone} ${
                featured ? 'text-sm sm:text-3xl' : 'text-xs sm:text-2xl'
              }`}
            >
              {formatLeaderboardPnl(pnl)}
            </p>
            <p className="mt-0.5 hidden text-[10px] font-medium uppercase tracking-[0.16em] text-muted sm:mt-1 sm:block sm:text-[11px]">
              Realized P&amp;L
            </p>

            <div className="mt-2 hidden w-full grid-cols-3 gap-2 border-t border-[#246BF5]/10 pt-3 dark:border-white/10 sm:mt-5 sm:grid sm:gap-3 sm:pt-4">
              <Metric label="Win rate" value={`${winRate.toFixed(1)}%`} />
              <Metric
                label="ROI"
                value={`${roi >= 0 ? '+' : ''}${roi.toFixed(1)}%`}
                tone={roiTone}
              />
              <Metric label="Trades" value={String(trader.trades ?? 0)} />
            </div>

            <div className="mt-1.5 flex w-full items-center justify-center gap-1.5 text-[9px] tabular-nums text-muted sm:hidden">
              <span>{winRate.toFixed(0)}%</span>
              <span className="text-border">·</span>
              <span className={roiTone === 'pos' ? 'text-emerald-600 dark:text-emerald-400' : roiTone === 'neg' ? 'text-red-600 dark:text-red-400' : ''}>
                {`${roi >= 0 ? '+' : ''}${roi.toFixed(0)}%`}
              </span>
            </div>

            <button
              type="button"
              onClick={() => onViewStats(trader)}
              disabled={!trader.masterId}
              className="mt-2 inline-flex w-full items-center justify-center gap-1 rounded-lg bg-[#246BF5] px-1.5 py-1.5 text-[10px] font-semibold text-white shadow-[0_8px_24px_rgba(36,107,245,0.28)] transition-all duration-300 hover:scale-[1.02] hover:bg-[#1d5ad4] hover:shadow-[0_10px_28px_rgba(36,107,245,0.4)] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40 sm:mt-5 sm:gap-2 sm:rounded-xl sm:px-4 sm:py-2.5 sm:text-sm"
            >
              <FiBarChart2 className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="sm:hidden">Stats</span>
              <span className="hidden sm:inline">View stats</span>
            </button>
          </div>
        </div>
      </article>
    </div>
  );
};

const CompactRow = ({ trader, onViewStats, platformLabel, index = 0 }) => {
  const pnl = Number(trader.pnl || 0);
  const roi = Number(trader.roi || 0);
  const winRate = Number(trader.winRate || 0);
  const pnlColor = pnl >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400';
  const roiColor = roi >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400';
  const PnlIcon = pnl >= 0 ? FiTrendingUp : FiTrendingDown;

  return (
    <div
      className="lb-anim-row group grid grid-cols-[2.25rem_minmax(0,1fr)_auto] items-center gap-2 rounded-xl border border-[#246BF5]/15 bg-white/80 px-2.5 py-3 shadow-[0_4px_16px_rgba(36,107,245,0.06)] backdrop-blur-md transition-all duration-300 hover:-translate-y-0.5 hover:border-[#246BF5]/35 hover:shadow-[0_10px_24px_rgba(36,107,245,0.12)] dark:bg-[#121722]/90 dark:hover:bg-[#151a24] dark:hover:shadow-[0_10px_28px_rgba(36,107,245,0.18)] sm:grid-cols-[3rem_minmax(0,1.4fr)_repeat(3,minmax(0,5.5rem))_auto] sm:gap-4 sm:px-4"
      style={{ animationDelay: `${220 + index * 60}ms` }}
    >
      <span className="text-center text-base font-bold tabular-nums text-muted transition-colors group-hover:text-[#246BF5] sm:text-lg">
        {trader.rank}
      </span>

      <div className="flex min-w-0 items-center gap-2.5 sm:gap-3">
        <Avatar
          trader={trader}
          size="sm"
          ringClass="ring-[#246BF5]/30"
          shadowClass="shadow-[0_0_12px_rgba(36,107,245,0.2)]"
        />
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="truncate text-sm font-semibold text-foreground sm:text-base">{trader.name}</p>
            <FiCheckCircle className="h-3.5 w-3.5 shrink-0 text-[#246BF5] dark:text-[#4f8cff]" aria-hidden />
          </div>
          <p className="mt-0.5 truncate text-[11px] text-muted sm:text-xs">
            <span className={`font-medium ${platformLabelClass(platformLabel)}`}>{platformLabel}</span>
            <span className="mx-1 text-border">·</span>
            <span className="inline-flex items-center gap-1">
              <FiActivity className="h-3 w-3 text-[#246BF5]/80" />
              {trader.trades}
            </span>
            <span className="mx-1 text-border">·</span>
            <span className="inline-flex items-center gap-1">
              <FiUsers className="h-3 w-3 text-[#246BF5]/80" />
              {Number(trader.followers || 0)}
            </span>
          </p>
        </div>
      </div>

      <div className="hidden text-right sm:block">
        <p className="text-sm font-semibold tabular-nums text-foreground">{winRate.toFixed(1)}%</p>
        <p className="text-[10px] uppercase tracking-wider text-muted">Win</p>
      </div>
      <div className="hidden text-right sm:block">
        <p className={`text-sm font-semibold tabular-nums ${roiColor}`}>
          {`${roi >= 0 ? '+' : ''}${roi.toFixed(1)}%`}
        </p>
        <p className="text-[10px] uppercase tracking-wider text-muted">ROI</p>
      </div>
      <div className="hidden text-right sm:block">
        <p className={`inline-flex items-center justify-end gap-1 text-sm font-semibold tabular-nums ${pnlColor}`}>
          <PnlIcon className="h-3.5 w-3.5" />
          {formatLeaderboardPnl(pnl)}
        </p>
        <p className="text-[10px] uppercase tracking-wider text-muted">P&amp;L</p>
      </div>

      <div className="flex items-center gap-2 justify-self-end">
        <span className={`text-xs font-semibold tabular-nums sm:hidden ${pnlColor}`}>
          {formatLeaderboardPnl(pnl)}
        </span>
        <button
          type="button"
          onClick={() => onViewStats(trader)}
          disabled={!trader.masterId}
          className="inline-flex items-center gap-1.5 rounded-lg border border-[#246BF5]/25 bg-[#246BF5]/8 px-2.5 py-2 text-xs font-semibold text-[#246BF5] shadow-[0_0_12px_rgba(36,107,245,0.1)] transition-all duration-300 hover:bg-[#246BF5]/15 disabled:opacity-40 dark:border-[#246BF5]/30 dark:bg-[#246BF5]/10 dark:text-[#4f8cff] sm:px-3"
        >
          <FiBarChart2 className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Stats</span>
        </button>
      </div>
    </div>
  );
};

const StatsModal = ({ statsTrader, onClose }) => {
  if (!statsTrader || typeof document === 'undefined') return null;
  return createPortal(
    <div className="fixed inset-0 z-[220] overflow-y-auto bg-slate-900/40 p-2 backdrop-blur-sm dark:bg-black/75 sm:p-4">
      <div className="mx-auto w-full max-w-[1200px] pt-6 sm:pt-8">
        <div className="lb-anim-rise relative w-full rounded-2xl border border-[#246BF5]/20 bg-white p-3 shadow-[0_24px_80px_rgba(36,107,245,0.15)] dark:border-[#246BF5]/25 dark:bg-gradient-to-b dark:from-[#151a24] dark:to-[#0f1420] dark:shadow-[0_24px_80px_rgba(36,107,245,0.2)] md:p-4">
          <div className="mb-3 flex items-start justify-between gap-3 sm:mb-4">
            <div className="min-w-0 flex-1 px-2 sm:px-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#246BF5] dark:text-[#4f8cff]">
                Performance detail
              </p>
              <h3 className="mt-1 text-lg font-semibold text-foreground sm:text-xl">{statsTrader.name}</h3>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-[#246BF5]/20 bg-surface text-foreground transition-colors hover:border-[#246BF5]/40 dark:border-white/10 dark:bg-[#0a0c10]"
              aria-label="Close stats modal"
            >
              <FiX className="h-4 w-4" />
            </button>
          </div>
          <ProfitLoss
            key={statsTrader.masterId}
            selectedBrokerId={null}
            masterUserId={statsTrader.masterId}
          />
        </div>
      </div>
    </div>,
    document.body
  );
};

const TopTraderLeaderboard = () => {
  const [activeTab, setActiveTab] = useState('webull');
  const [statsTrader, setStatsTrader] = useState(null);

  const activeConfig =
    MASTER_LEADERBOARD_TABS.find((t) => t.id === activeTab) || MASTER_LEADERBOARD_TABS[0];
  const isForexGoldTab = activeTab === 'forex-gold';
  const platformLabel = isForexGoldTab ? 'Forex Gold' : 'Webull';

  const leaderboardQuery = useApi.useTopTraderLeaderboard(activeConfig.brokerType);

  const rankedTraders = useMemo(() => {
    const apiRows = leaderboardQuery.data?.rows || [];
    return apiRows.map((row) => ({
      rank: row.rank,
      name: row.name,
      avatar: row.avatar || '',
      market: row.market || platformLabel,
      pnl: row.pnl,
      roi: row.roi,
      winRate: row.winRate,
      trades: row.trades,
      followers: row.followers,
      status: row.status,
      masterId: row.masterId,
    }));
  }, [leaderboardQuery.data?.rows, platformLabel]);

  const first = rankedTraders.find((t) => t.rank === 1) || rankedTraders[0];
  const second = rankedTraders.find((t) => t.rank === 2) || rankedTraders[1];
  const third = rankedTraders.find((t) => t.rank === 3) || rankedTraders[2];
  const rowsBelow = rankedTraders.filter((t) => t !== first && t !== second && t !== third);

  const isLoading = leaderboardQuery.isLoading;
  const isError = leaderboardQuery.isError;
  const titleMarket = isForexGoldTab ? 'Forex Gold' : 'Webull';

  return (
    <div className="animate-fade-in w-full min-w-0">
      <div className="relative w-full overflow-hidden rounded-2xl border border-[#246BF5]/20 bg-gradient-to-br from-[#f4f8ff] via-[#eef4ff] to-white shadow-[0_0_40px_rgba(36,107,245,0.08)] dark:from-[#0f1420] dark:via-[#0c1018] dark:to-[#0a0c10] dark:shadow-[0_0_40px_rgba(36,107,245,0.1)]">
        <div
          className="pointer-events-none absolute inset-0 dark:hidden"
          style={{
            background:
              'radial-gradient(900px 320px at 12% -10%, rgba(36,107,245,0.14), transparent 55%), radial-gradient(700px 280px at 92% 0%, rgba(79,140,255,0.1), transparent 50%)',
          }}
        />
        <div
          className="pointer-events-none absolute inset-0 hidden dark:block"
          style={{
            background:
              'radial-gradient(900px 320px at 12% -10%, rgba(36,107,245,0.2), transparent 55%), radial-gradient(700px 280px at 92% 0%, rgba(79,140,255,0.12), transparent 50%), radial-gradient(500px 220px at 50% 40%, rgba(36,107,245,0.06), transparent 60%)',
          }}
        />

        <div className="relative z-10 mx-auto max-w-6xl px-3 py-6 sm:px-6 sm:py-8 md:px-8 md:py-10">
          <header className="!mb-10 lb-anim-rise relative overflow-hidden rounded-xl border border-[#246BF5]/15 bg-white/70 px-3.5 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_8px_24px_rgba(36,107,245,0.08)] backdrop-blur-2xl dark:border-white/10 dark:bg-white/[0.04] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_8px_28px_rgba(36,107,245,0.1)] sm:mb-6 sm:px-4 sm:py-3.5">
            <div
              className="pointer-events-none absolute inset-0 opacity-80"
              style={{
                background:
                  'radial-gradient(420px 120px at 6% 0%, rgba(36,107,245,0.12), transparent 55%)',
              }}
            />
            <div className="pointer-events-none absolute inset-x-4 top-0 h-px bg-gradient-to-r from-transparent via-[#246BF5]/30 to-transparent dark:via-white/20" />

            <div className="relative flex flex-row items-center justify-between gap-2 sm:gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                  <span className="inline-flex items-center gap-1.5 rounded-md border border-[#246BF5]/25 bg-[#246BF5]/8 px-2 py-0.5 dark:border-[#246BF5]/30 dark:bg-[#246BF5]/10">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#246BF5]" />
                    <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#246BF5] dark:text-[#4f8cff]">
                      Rankings
                    </span>
                  </span>
                  <p className="hidden text-xs text-muted sm:inline">
                    Verified realized performance
                  </p>
                </div>
                <h1 className="mt-1.5 truncate text-base font-semibold tracking-tight text-foreground sm:text-2xl">
                  {titleMarket}{' '}
                  <span className="font-semibold text-[#246BF5]">leaderboard</span>
                </h1>
              </div>

              <div className="w-auto shrink-0">
                <LeaderboardMarketTabs
                  activeTab={activeTab}
                  onChange={(tabId) => {
                    setActiveTab(tabId);
                    setStatsTrader(null);
                  }}
                />
              </div>
            </div>
          </header>

          {isLoading && (
            <div className="mb-6 rounded-xl border border-[#246BF5]/20 bg-white/70 px-4 py-8 text-center text-sm text-muted dark:bg-[#121722]">
              Loading leaderboard…
            </div>
          )}
          {isError && (
            <div className="mb-6 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-center text-sm text-red-600 dark:text-red-300">
              Failed to load leaderboard. Please try again.
            </div>
          )}
          {!isLoading && !isError && rankedTraders.length === 0 && (
            <div className="mb-6 rounded-xl border border-dashed border-[#246BF5]/25 bg-white/60 px-4 py-12 text-center text-sm text-muted dark:bg-[#121722]/60">
              No {isForexGoldTab ? 'Genesis' : 'Webull'} masters found yet.
            </div>
          )}

          {(first || second || third) && (
            <section className="mb-8 sm:mb-10" aria-label="Top three traders">
              <div className="grid grid-cols-3 items-end gap-1.5 sm:gap-4 md:gap-5">
                <div className="min-w-0">
                  {second && (
                    <PodiumCard
                      trader={second}
                      rank={2}
                      platformLabel={second.market || platformLabel}
                      onViewStats={setStatsTrader}
                    />
                  )}
                </div>
                <div className="min-w-0">
                  {first && (
                    <PodiumCard
                      trader={first}
                      rank={1}
                      featured
                      platformLabel={first.market || platformLabel}
                      onViewStats={setStatsTrader}
                    />
                  )}
                </div>
                <div className="min-w-0">
                  {third && (
                    <PodiumCard
                      trader={third}
                      rank={3}
                      platformLabel={third.market || platformLabel}
                      onViewStats={setStatsTrader}
                    />
                  )}
                </div>
              </div>
            </section>
          )}

          {rowsBelow.length > 0 && (
            <section aria-label="Remaining rankings">
              <div className="mb-3 flex items-center justify-between gap-3">
                <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-[#246BF5] dark:text-[#4f8cff]/90">
                  Full standings
                </h2>
                <p className="text-xs text-muted">{rowsBelow.length} traders</p>
              </div>
              <div className="space-y-2">
                {rowsBelow.map((trader, index) => (
                  <CompactRow
                    key={trader.masterId || trader.rank}
                    trader={trader}
                    index={index}
                    platformLabel={trader.market || platformLabel}
                    onViewStats={setStatsTrader}
                  />
                ))}
              </div>
            </section>
          )}

          <StatsModal statsTrader={statsTrader} onClose={() => setStatsTrader(null)} />
        </div>
      </div>
    </div>
  );
};

export default TopTraderLeaderboard;
